import React from "react"
import styles from "./ProgressReportPdf.module.css"
import {get} from "lodash"
import {DegreeAuditProgressData} from "types/degreeAudit"
import {Auth} from "types/auth"
import {useModel} from "hooks"
import {formatDateOnly, getFieldLabel, roundTwoDigits} from "helpers"
import {Settings} from "types/settings"

import {useTranslation} from "react-i18next"

type StudentInfoCreditProps = {
    progressData: DegreeAuditProgressData
    studentInfo: Auth.DepartmentStudent
    cgpa: number
}

const StudentInfoCredit = (props: StudentInfoCreditProps) => {
    const model = useModel()
    const {studentInfo, progressData, cgpa} = props
    const {isDropped, isCancelled, withdrawalDate, cancelDate} = studentInfo || {}
    const dateFormat = model.getUserDateFormat()
    const academicStatus = get(studentInfo, ["status", "name"], "")
    const {t} = useTranslation(["common", "user"])

    return (
        <div className={styles.studentInfo}>
            <span className={styles.degreeText}>DEGREE</span>
            <div className={styles.studentInfoDetail}>
                <div className={styles.blockDetail}>
                    <div className={styles.sectionDetail}>
                        <span className={styles.titleInfo}>DOB</span>
                        <span className={styles.textInfo}>{formatDateOnly(studentInfo?.birthDate, dateFormat)}</span>
                    </div>
                    <div className={styles.sectionDetail}>
                        <span className={styles.titleInfo}>
                            {getFieldLabel(model, Settings.GeneralLabel.Program, t("studentInfo.program"))}
                        </span>
                        <span className={styles.textInfo}>{get(studentInfo, ["programName"], "")}</span>
                    </div>
                    <div className={styles.sectionDetail}>
                        <span className={styles.titleInfo}>Degree credentials</span>
                        <span className={styles.textInfo}>{get(studentInfo, ["degreeCredential"], "")}</span>
                    </div>
                    <div className={styles.sectionDetail}>
                        <span className={styles.titleInfo}>
                            {getFieldLabel(model, Settings.GeneralLabel.Campus, t("studentInfo.campus"))}
                        </span>
                        <span className={styles.textInfo}>{get(studentInfo, ["campuses", 0], "")}</span>
                    </div>
                    <div className={styles.sectionDetail}>
                        <span className={styles.titleInfo}>
                            {getFieldLabel(model, Settings.GeneralLabel.Campus, t("studentInfo.campus"))} address
                        </span>
                        <span className={styles.textInfo}>{get(studentInfo, ["campusAddresses", 0], "")}</span>
                    </div>
                    <div className={styles.sectionDetail}>
                        <span className={styles.titleInfo}>Status</span>
                        {academicStatus?.length ? (
                            <div className={styles.statusInfo}>
                                <span className={styles.statusText}>{academicStatus}</span>
                            </div>
                        ) : (
                            <span className={styles.textInfo} />
                        )}
                    </div>
                </div>

                <div className={styles.blockDetail}>
                    <div className={styles.sectionDetail}>
                        <span className={styles.titleInfo}>
                            {getFieldLabel(model, Settings.GeneralLabel.StartDate, t("studentInfo.startDate"))}
                        </span>
                        <span className={styles.textInfo}>{formatDateOnly(studentInfo?.startDate, dateFormat)}</span>
                    </div>
                    <div className={styles.sectionDetail}>
                        <span className={styles.titleInfo}>
                            {getFieldLabel(model, Settings.GeneralLabel.Lda, t("studentInfo.lastDayOfAttendance"))}
                        </span>
                        <span className={styles.textInfo}>
                            {studentInfo?.lastDayOfAttendance
                                ? formatDateOnly(studentInfo.lastDayOfAttendance, dateFormat)
                                : ""}
                        </span>
                    </div>
                    <div className={styles.sectionDetail}>
                        <span className={styles.titleInfo}>Contracted Grad Date</span>
                        <span className={styles.textInfo}>
                            {formatDateOnly(studentInfo?.contractEndDate, dateFormat)}
                        </span>
                    </div>
                    <div className={styles.sectionDetail}>
                        <span className={styles.titleInfo}>Revised Grad Date</span>
                        <span className={styles.textInfo}>
                            {formatDateOnly(studentInfo?.projectedEndDate, dateFormat)}
                        </span>
                    </div>
                    <div className={styles.sectionDetail}>
                        <span className={styles.titleInfo}>Conferred Date</span>
                        <span className={styles.textInfo}>
                            {formatDateOnly(studentInfo?.degreeConferredDate, dateFormat, "NA")}
                        </span>
                    </div>
                    {isDropped || isCancelled ? (
                        <div className={styles.sectionDetail}>
                            <span className={styles.titleInfo}>Withdrawal Date</span>
                            <span className={styles.textInfo}>
                                {formatDateOnly(withdrawalDate || cancelDate, dateFormat)}
                            </span>
                        </div>
                    ) : null}
                    <div className={styles.sectionDetail}>
                        <span className={styles.titleInfo}>
                            {getFieldLabel(model, Settings.AcademicLabel.Cgpa, "CGPA")}
                        </span>
                        <span className={styles.textInfo}>{cgpa}</span>
                    </div>
                    <div className={styles.sectionDetail}>
                        <span className={styles.titleInfo}>Weekly Sched Hours</span>
                        <span className={styles.textInfo}>
                            {roundTwoDigits(studentInfo?.programVersionNumberOfHoursPerWeek ?? 0)}
                        </span>
                    </div>
                </div>

                <div className={styles.blockDetail}>
                    <div className={styles.sectionDetail}>
                        <span className={styles.titleInfo}>Total Program Hours</span>
                        <span className={styles.textInfo}>{roundTwoDigits(progressData?.degreeRequirement ?? 0)}</span>
                    </div>
                    <div className={styles.sectionDetail}>
                        <span className={styles.titleInfo}>Credits attempted</span>
                        <span className={styles.textInfo}>{roundTwoDigits(progressData?.attempted ?? 0)}</span>
                    </div>
                    <div className={styles.sectionDetail}>
                        <span className={styles.titleInfo}>Credits Earned</span>
                        <span className={styles.textInfo}>{roundTwoDigits(progressData?.completed ?? 0)}</span>
                    </div>
                    <div className={styles.sectionDetail}>
                        <span className={styles.titleInfo}>Transfer Credits</span>
                        <span className={styles.textInfo}>
                            {roundTwoDigits(progressData?.totalTransferApplied ?? 0)}
                        </span>
                    </div>
                    <div className={styles.sectionDetail}>
                        <span className={styles.titleInfo}>Previously Completed</span>
                        <span className={styles.textInfo}>
                            {roundTwoDigits(progressData?.totalPreviousCompletedApplied ?? 0)}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default StudentInfoCredit
